
(() => {
  'use strict';

  function initFxProduct(section) {
    if (!section || section.dataset.fxInitialized === 'true') return;
    section.dataset.fxInitialized = 'true';

    const readJSON = (selector, fallback = null) => {
      const element = section.querySelector(selector);
      if (!element) return fallback;

      try {
        return JSON.parse(element.textContent);
      } catch {
        return fallback;
      }
    };

    const variants = readJSON('[data-fx-variants]', []);
    const optionNames = readJSON('[data-fx-product-options]', []);
    const initialVariant = readJSON('[data-fx-initial-variant]', null);

    const form = section.querySelector('.fx-product-form');
    const variantInput = section.querySelector('[data-fx-variant-id]');
    const priceEl = section.querySelector('[data-fx-price]');
    const compareEl = section.querySelector('[data-fx-compare]');
    const stockEl = section.querySelector('[data-fx-stock]');
    const cartButton = section.querySelector('[data-fx-cart]');
    const buyButton = section.querySelector('[data-fx-buy-now]');
    const quantityInput = section.querySelector('[data-fx-quantity]');
    const mainImage = section.querySelector('[data-fx-main-image]');

    if (!variants.length || !form || !variantInput) return;

    const comingSoon = section.dataset.comingSoon === 'true';
    const moneyFormat = section.dataset.moneyFormat || 'R{{amount}}';

    let currentVariant =
      variants.find(variant => String(variant.id) === String(variantInput.value)) ||
      initialVariant ||
      variants[0];

    function formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, moneyFormat);
      }

      return new Intl.NumberFormat('en-ZA', {
        style: 'currency',
        currency: 'ZAR'
      }).format(Number(cents || 0) / 100);
    }

    function getSelectedOptions() {
      return optionNames.map((_, index) => {
        const active = section.querySelector(
          `[data-fx-swatch][data-option-index="${index}"].is-active`
        );

        return active ? active.dataset.value : null;
      });
    }

    function findMatchingVariant() {
      const selected = getSelectedOptions();

      return variants.find(variant =>
        variant.options.every((value, index) =>
          !selected[index] || value === selected[index]
        )
      );
    }

    function updatePrice(variant) {
      if (priceEl) {
        priceEl.textContent = formatMoney(variant.price);
      }

      if (compareEl) {
        if (
          variant.compare_at_price &&
          variant.compare_at_price > variant.price
        ) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.classList.remove('is-hidden');
        } else {
          compareEl.textContent = '';
          compareEl.classList.add('is-hidden');
        }
      }
    }

    function updateAvailability(variant) {
      if (stockEl) {
        stockEl.textContent = comingSoon
          ? 'Coming soon'
          : variant.available
            ? 'In stock and ready to ship'
            : 'Sold out';

        stockEl.classList.toggle(
          'is-sold-out',
          !variant.available && !comingSoon
        );
      }

      if (cartButton) {
        cartButton.disabled = comingSoon || !variant.available;
        cartButton.textContent = comingSoon
          ? 'Coming soon'
          : variant.available
            ? 'Add to cart'
            : 'Sold out';
      }

      if (buyButton) {
        buyButton.disabled = comingSoon || !variant.available;
        buyButton.textContent = comingSoon ? 'Coming soon' : 'Buy it now';
      }
    }

    function updateSelectedLabels() {
      getSelectedOptions().forEach((value, index) => {
        const label = section.querySelector(
          `[data-fx-selected-label="${index}"]`
        );

        if (label && value) label.textContent = value;
      });
    }

    function updateImage(variant) {
      if (!mainImage || !variant.featured_image) return;

      const imageUrl =
        typeof variant.featured_image === 'string'
          ? variant.featured_image
          : variant.featured_image.src;

      if (!imageUrl) return;

      mainImage.src = imageUrl;
      mainImage.alt = variant.public_title || variant.name || '';

      section.querySelectorAll('[data-fx-thumbnail]').forEach(button => {
        const active = button.dataset.image === imageUrl;

        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    function updateSwatchAvailability() {
      const selected = getSelectedOptions();

      section.querySelectorAll('[data-fx-swatch]').forEach(button => {
        const optionIndex = Number(button.dataset.optionIndex);
        const value = button.dataset.value;

        const possible = variants.some(variant => {
          if (!variant.available) return false;

          return variant.options.every((optionValue, index) => {
            if (index === optionIndex) return optionValue === value;

            return !selected[index] || optionValue === selected[index];
          });
        });

        button.disabled = !possible;
      });
    }

    function updateURL(variant) {
      if (!variant || !window.history?.replaceState) return;

      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (error) {
        console.warn('FX Co: Could not update variant URL.', error);
      }
    }

    function applyVariant(variant, updateUrl = true) {
      if (!variant) return;

      currentVariant = variant;
      variantInput.value = variant.id;

      updatePrice(variant);
      updateAvailability(variant);
      updateImage(variant);
      updateSelectedLabels();
      updateSwatchAvailability();

      if (updateUrl) updateURL(variant);

      section.dispatchEvent(new CustomEvent('fx:variant:change', {
        bubbles: true,
        detail: { variant }
      }));
    }

    function selectInitialSwatches(variant) {
      if (!variant || !Array.isArray(variant.options)) return;

      variant.options.forEach((value, index) => {
        section.querySelectorAll(
          `[data-fx-swatch][data-option-index="${index}"]`
        ).forEach(button => {
          const active = button.dataset.value === value;

          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
      });
    }

    section.querySelectorAll('[data-fx-swatch]').forEach(button => {
      button.addEventListener('click', () => {
        if (button.disabled) return;

        const optionIndex = Number(button.dataset.optionIndex);

        section.querySelectorAll(
          `[data-fx-swatch][data-option-index="${optionIndex}"]`
        ).forEach(swatch => {
          swatch.classList.remove('is-active');
          swatch.setAttribute('aria-pressed', 'false');
        });

        button.classList.add('is-active');
        button.setAttribute('aria-pressed', 'true');

        const variant = findMatchingVariant();

        if (variant) {
          applyVariant(variant);
        } else {
          if (cartButton) {
            cartButton.disabled = true;
            cartButton.textContent = 'Unavailable';
          }

          if (buyButton) buyButton.disabled = true;

          if (stockEl) {
            stockEl.textContent = 'This combination is unavailable';
          }

          updateSelectedLabels();
          updateSwatchAvailability();
        }
      });
    });

    section.querySelectorAll('[data-fx-thumbnail]').forEach(button => {
      button.addEventListener('click', () => {
        if (!mainImage) return;

        mainImage.src = button.dataset.image;
        mainImage.alt = button.dataset.alt || '';

        section.querySelectorAll('[data-fx-thumbnail]').forEach(thumbnail => {
          const active = thumbnail === button;

          thumbnail.classList.toggle('is-active', active);
          thumbnail.setAttribute('aria-pressed', String(active));
        });
      });
    });

    const minusButton = section.querySelector('[data-fx-quantity-minus]');
    const plusButton = section.querySelector('[data-fx-quantity-plus]');

    if (minusButton && quantityInput) {
      minusButton.addEventListener('click', () => {
        quantityInput.value = Math.max(
          1,
          (parseInt(quantityInput.value, 10) || 1) - 1
        );

        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    if (plusButton && quantityInput) {
      plusButton.addEventListener('click', () => {
        quantityInput.value =
          Math.max(1, parseInt(quantityInput.value, 10) || 1) + 1;

        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    if (quantityInput) {
      quantityInput.addEventListener('change', () => {
        quantityInput.value = Math.max(
          1,
          parseInt(quantityInput.value, 10) || 1
        );
      });
    }

    if (comingSoon) {
      if (stockEl) stockEl.textContent = 'Coming soon';

      if (cartButton) {
        cartButton.disabled = true;
        cartButton.textContent = 'Coming soon';
      }

      if (buyButton) {
        buyButton.disabled = true;
        buyButton.textContent = 'Coming soon';
      }
    }

    selectInitialSwatches(currentVariant);
    applyVariant(currentVariant, false);
  }

  function initAllFxProducts() {
    document.querySelectorAll('.fx-product').forEach(initFxProduct);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllFxProducts);
  } else {
    initAllFxProducts();
  }

  document.addEventListener('shopify:section:load', event => {
    const target = event.target;

    const section = target.matches?.('.fx-product')
      ? target
      : target.querySelector?.('.fx-product');

    if (section) {
      section.dataset.fxInitialized = 'false';
      initFxProduct(section);
    }
  });
})();
